import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { getLang, t } from "@/lib/i18n";
import { Clock, ArrowRight, ArrowLeft } from "lucide-react";
import QuestionImage from "@/components/QuestionImage";
import { toast } from "sonner";

export default function MockExam() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const lang = getLang();
  const [exam, setExam] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(30 * 60);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem("tvde_exam");
    if (stored) {
      const d = JSON.parse(stored);
      if (d.attempt_id === attemptId) {
        setExam(d);
        setSecondsLeft(d.duration_min * 60);
        return;
      }
    }
    toast.error("Exam session not found. Restart from category.");
    nav("/categories");
  }, [attemptId, nav]);

  useEffect(() => {
    if (!exam) return;
    const iv = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(iv);
  }, [exam]);

  useEffect(() => {
    if (secondsLeft === 0 && exam) doSubmit();
    // eslint-disable-next-line
  }, [secondsLeft]);

  const doSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post("/mock-exam/submit", { attempt_id: exam.attempt_id, answers });
      sessionStorage.setItem("tvde_result", JSON.stringify(r.data));
      sessionStorage.removeItem("tvde_exam");
      nav(`/exam-results/${exam.attempt_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
      setSubmitting(false);
    }
  };

  const mmss = useMemo(() => {
    const m = Math.floor(secondsLeft / 60).toString().padStart(2, "0");
    const s = (secondsLeft % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }, [secondsLeft]);

  if (!exam) return <Layout><div className="max-w-3xl mx-auto px-6 py-16">Loading exam...</div></Layout>;

  const q = exam.questions[idx];
  const total = exam.questions.length;
  const answered = Object.keys(answers).length;
  const progress = ((idx + 1) / total) * 100;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-8 fade-in">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            {t(lang, "exam")} · <span data-testid="exam-progress">{idx + 1} / {total}</span>
          </div>
          <div className={`font-mono text-lg font-semibold flex items-center gap-2 ${secondsLeft < 60 ? "text-red-600" : "text-foreground"}`} data-testid="exam-timer">
            <Clock className="w-4 h-4" /> {mmss}
          </div>
        </div>
        <Progress value={progress} className="h-1 mb-8" />

        <h2 className="font-serif text-2xl sm:text-3xl font-medium leading-tight mb-6" data-testid="exam-question-text">{q.text_pt}</h2>

        <QuestionImage qid={q.id} imageFile={q.image_file} />

        <div className="space-y-3">
          {q.options_pt.map((o) => {
            const isSelected = answers[q.id] === o.letter;
            return (
              <button key={o.letter}
                data-testid={`exam-option-${o.letter}`}
                onClick={() => setAnswers({ ...answers, [q.id]: o.letter })}
                className={`w-full text-left px-5 py-4 border-2 rounded-md transition-colors flex items-start gap-4 ${isSelected ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/8" : "border-border hover:border-foreground/40"}`}
              >
                <div className={`font-mono text-sm font-semibold w-6 ${isSelected ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`}>{o.letter}</div>
                <div className="flex-1 text-sm leading-relaxed">{o.text}</div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-border">
          <Button variant="ghost" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="exam-prev">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t(lang, "prev")}
          </Button>
          <div className="text-sm text-muted-foreground font-mono">{answered}/{total} answered</div>
          {idx < total - 1 ? (
            <Button onClick={() => setIdx(idx + 1)} className="bg-foreground text-background rounded-full" data-testid="exam-next">
              {t(lang, "next")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={doSubmit} disabled={submitting} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 rounded-full" data-testid="exam-submit">
              {t(lang, "submit")}
            </Button>
          )}
        </div>

        {/* Question grid nav */}
        <div className="mt-10 grid grid-cols-10 gap-1.5" data-testid="exam-grid">
          {exam.questions.map((qq, i) => (
            <button key={qq.id} onClick={() => setIdx(i)}
              data-testid={`exam-grid-${i}`}
              className={`h-8 rounded-sm text-xs font-mono border transition-colors ${
                i === idx ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
                : answers[qq.id] ? "border-foreground/70 bg-foreground/10"
                : "border-border hover:border-foreground/40"
              }`}
            >{i + 1}</button>
          ))}
        </div>
      </div>
    </Layout>
  );
}
